// Client-side branded HTML export utilities (download + print-to-PDF friendly).

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const SHELL = (title, bodyHtml) => `<!doctype html>
<html lang="en"><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${esc(title)}</title>
<style>
  :root { --teal:#2dd4bf; --ink:#0f172a; --muted:#64748b; --line:#e2e8f0; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: var(--ink); margin: 0; background: #f8fafc; }
  .wrap { max-width: 900px; margin: 0 auto; padding: 40px 28px 64px; }
  header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid var(--teal); padding-bottom: 16px; margin-bottom: 24px; }
  .brand { font-weight: 800; font-size: 22px; letter-spacing: -.5px; }
  .brand span { color: var(--teal); }
  .meta { color: var(--muted); font-size: 12px; text-align: right; }
  h1 { font-size: 26px; margin: 0 0 4px; }
  .sub { color: var(--muted); font-size: 14px; margin-bottom: 24px; }
  .card { background: #fff; border: 1px solid var(--line); border-radius: 12px; padding: 18px 20px; margin-bottom: 14px; page-break-inside: avoid; }
  .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
  .name { font-weight: 700; font-size: 16px; }
  .head { color: var(--muted); font-size: 13px; }
  .score { font-weight: 800; color: var(--teal); font-size: 15px; white-space: nowrap; }
  .chips { margin-top: 10px; }
  .chip { display: inline-block; font-size: 11px; background: #f0fdfa; color: #0f766e; border: 1px solid #99f6e4; border-radius: 999px; padding: 2px 8px; margin: 0 4px 4px 0; }
  .field { font-size: 13px; color: #334155; margin-top: 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--line); }
  th { background: #f1f5f9; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; }
  .status { font-weight: 600; text-transform: capitalize; }
  .pricing { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
  .plan { background: #fff; border: 1px solid var(--line); border-radius: 12px; padding: 18px; }
  .plan.pop { border: 2px solid var(--teal); }
  .price { font-weight: 800; font-size: 24px; margin: 8px 0; }
  .plan ul { padding-left: 18px; margin: 8px 0 0; color: #334155; font-size: 12px; }
  footer { margin-top: 32px; color: var(--muted); font-size: 11px; text-align: center; border-top: 1px solid var(--line); padding-top: 16px; }
  .print { position: fixed; top: 16px; right: 16px; background: var(--teal); color: #06251f; border: none; border-radius: 8px; padding: 10px 16px; font-weight: 700; cursor: pointer; }
  @media print { .print { display: none; } body { background: #fff; } .wrap { padding: 0; } }
</style></head>
<body>
  <button class="print" onclick="window.print()">Print / Save as PDF</button>
  <div class="wrap">
    <header>
      <div class="brand">WorkOn<span>iT</span></div>
      <div class="meta">Generated ${esc(new Date().toLocaleString())}<br/>WorkOnIT — a directory & platform</div>
    </header>
    ${bodyHtml}
    <footer>WorkOnIT is a directory and platform, not an employment agency. Managed by WorkOnIT, owned by JamesBass Technologies (a division of JamesBass Holding LLC).</footer>
  </div>
</body></html>`;

function triggerDownload(html, filename) {
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const candidateCardHtml = (c) => `
  <div class="card">
    <div class="row">
      <div>
        <div class="name">${esc(c.name)}</div>
        <div class="head">${esc(c.headline || "Candidate")}${c.location ? " · " + esc(c.location) : ""}</div>
      </div>
      ${typeof c.match_score === "number" ? `<div class="score">${c.match_score}% match</div>` : ""}
    </div>
    ${c.bio ? `<div class="field">${esc(c.bio)}</div>` : ""}
    ${(c.skills || []).length ? `<div class="chips">${c.skills.map((s) => `<span class="chip">${esc(s)}</span>`).join("")}</div>` : ""}
    ${(c.experiences || []).length ? `<div class="field"><strong>Experience:</strong> ${c.experiences.map((e) => esc(`${e.title || ""}${e.company ? " @ " + e.company : ""}`)).join("; ")}</div>` : ""}
  </div>`;

export function exportCandidatesHtml(candidates, title = "Candidate Shortlist") {
  const body = `
    <h1>${esc(title)}</h1>
    <div class="sub">${candidates.length} candidate${candidates.length !== 1 ? "s" : ""}</div>
    ${candidates.map(candidateCardHtml).join("")}`;
  triggerDownload(SHELL(title, body), `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.html`);
}

export function exportCandidateHtml(c) {
  const title = `${c.name} — Candidate Profile`;
  const body = `
    <h1>${esc(c.name)}</h1>
    <div class="sub">${esc(c.headline || "Candidate")}${c.location ? " · " + esc(c.location) : ""}</div>
    ${candidateCardHtml(c)}`;
  triggerDownload(SHELL(title, body), `${String(c.name || "candidate").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-profile.html`);
}

export function exportSubmissionsHtml(subs) {
  const LABEL = { submitted: "Submitted", client_review: "Client review", interview: "Interview", placed: "Placed", rejected: "Rejected" };
  const title = "Candidate Submissions Report";
  const body = `
    <h1>${esc(title)}</h1>
    <div class="sub">${subs.length} submission${subs.length !== 1 ? "s" : ""} · ${subs.filter((s) => s.status === "placed").length} placed</div>
    <table>
      <thead><tr><th>Candidate</th><th>Client</th><th>Role</th><th>Status</th><th>Submitted</th></tr></thead>
      <tbody>
        ${subs.map((s) => `<tr>
          <td>${esc(s.candidate_name)}<br/><span class="head">${esc(s.candidate_headline || "")}</span></td>
          <td>${esc(s.client_name)}</td>
          <td>${esc(s.job_title || "—")}</td>
          <td class="status">${esc(LABEL[s.status] || s.status)}</td>
          <td>${esc((s.created_at || "").slice(0, 10))}</td>
        </tr>`).join("")}
      </tbody>
    </table>`;
  triggerDownload(SHELL(title, body), "submissions-report.html");
}

export function exportPlansHtml(plans) {
  const title = "WorkOnIT Plans & Pricing";
  const grp = (label, list) => `
    <h1 style="margin-top:28px">${esc(label)}</h1>
    <div class="pricing">
      ${list.map((p) => `<div class="plan ${p.recommended ? "pop" : ""}">
        <div class="name">${esc(p.name)}${p.recommended ? " ★" : ""}</div>
        <div class="head">${esc(p.tagline || "")}</div>
        <div class="price">€${p.price_eur.toLocaleString()}<span class="head"> /mo</span></div>
        <div class="head">${p.cross_country ? "Cross-country EU search" : "Local candidates"}</div>
        <ul>${p.features.map((f) => `<li>${esc(f)}</li>`).join("")}</ul>
      </div>`).join("")}
    </div>`;
  const body = `
    <div class="sub">Your hiring engine — get qualified candidates, not just a dashboard. 30-day free trial.</div>
    ${grp("For Companies", plans.company)}
    ${grp("For Agencies", plans.agency)}`;
  triggerDownload(SHELL(title, body), "workonit-plans.html");
}
